import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type AppNameValue,
  type CreateOrganisationDocumentTemplateRequest,
  type OrganisationDocumentTemplate,
  type UpdateOrganisationDocumentTemplateRequest,
} from '../client';

/**
 * Query keys for document layouts. Scoped by app as well as organisation: the
 * apps print different documents, so a TMS screen invalidating its own list must
 * not blow away a Depot one sitting in the same cache.
 */
export const organisationDocumentTemplateKeys = {
  all: ['directory', 'organisationDocumentTemplates'] as const,
  byOrg: (organisationId: string, app?: AppNameValue) =>
    ['directory', 'organisationDocumentTemplates', organisationId, app ?? 'all'] as const,
};

/**
 * An organisation's document layouts, optionally for one app.
 *
 * `layoutJson` comes back as an opaque string — parse and migrate it on the
 * consuming side (`schemaVersion` says which shape it was saved in). Keeping it
 * a string here means this hook never needs to know what a layout contains.
 */
export function useOrganisationDocumentTemplates(
  organisationId: string | null,
  app?: AppNameValue,
  options?: Omit<
    UseQueryOptions<OrganisationDocumentTemplate[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrganisationDocumentTemplate[], Error>({
    queryKey: organisationId
      ? organisationDocumentTemplateKeys.byOrg(organisationId, app)
      : organisationDocumentTemplateKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationDocumentTemplates(organisationId, app);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Every write invalidates the whole template list rather than patching the
 * cache. The server owns two things a client-side patch would get wrong — which
 * layout is default after a promotion or a delete, and the `updatedUtc` stamp
 * the builder uses to reload its draft — so a refetch is the honest move.
 */
function useTemplateInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: organisationDocumentTemplateKeys.all });
}

export function useCreateOrganisationDocumentTemplate() {
  const invalidate = useTemplateInvalidator();
  return useMutation<string, Error, CreateOrganisationDocumentTemplateRequest>({
    mutationKey: [...organisationDocumentTemplateKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createOrganisationDocumentTemplate(body),
    onSuccess: invalidate,
  });
}

export function useUpdateOrganisationDocumentTemplate() {
  const invalidate = useTemplateInvalidator();
  return useMutation<string, Error, UpdateOrganisationDocumentTemplateRequest>({
    mutationKey: [...organisationDocumentTemplateKeys.all, 'update'],
    mutationFn: (body) => directoryApi.updateOrganisationDocumentTemplate(body),
    onSuccess: invalidate,
  });
}

export function useSetDefaultOrganisationDocumentTemplate() {
  const invalidate = useTemplateInvalidator();
  return useMutation<string, Error, string>({
    mutationKey: [...organisationDocumentTemplateKeys.all, 'set-default'],
    mutationFn: (id) => directoryApi.setDefaultOrganisationDocumentTemplate(id),
    onSuccess: invalidate,
  });
}

export function useDeleteOrganisationDocumentTemplate() {
  const invalidate = useTemplateInvalidator();
  return useMutation<string, Error, string>({
    mutationKey: [...organisationDocumentTemplateKeys.all, 'delete'],
    mutationFn: (id) => directoryApi.deleteOrganisationDocumentTemplate(id),
    onSuccess: invalidate,
  });
}
